import { useMemo, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, Minus, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { SiteLayout } from "@/components/SiteLayout";
import { Section } from "@/components/Section";
import { PageHero } from "@/components/PageHero";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { useCustomerSession } from "@/hooks/useCustomerSession";
import { HP_MAX_QTY, useHealthCart } from "@/hooks/useHealthCart";
import { formatPaise } from "@/lib/format/money";
import { cartTotals, CHECKOUT_BLOCKER_TEXT, orderErrorText, priceLine, type ShippingConfig } from "@/lib/health-products/pricing";
import { healthProductsQuery } from "@/routes/health-products.index";
import { packLabel } from "@/components/health-products/ProductCard";
import { loadRazorpayScript } from "@/lib/payments/razorpay-client";
import {
  confirmProductRazorpayPayment,
  reportProductRazorpayFailure,
  startProductRazorpayPayment,
} from "@/lib/health-products/orders.functions";

const TITLE = "Your Cart · Health Products | Poonam Kapur Healthy Kitchen";
const DESC = "Review your health products and place your order with Poonam Kapur Healthy Kitchen.";

export const Route = createFileRoute("/health-products/cart")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: HealthCartPage,
});

type CheckoutStatus = ShippingConfig & {
  blocker: string | null;
  delivery_scope: string | null;
  methods: { razorpay: boolean; cash: boolean; cheque: boolean };
  cash_min_order_paise: number | null;
};
type Method = "razorpay" | "cash" | "cheque";
type Placed = { order_id: string; order_number: string; total_paise: number; payment_method: Method; payment_status: string };

const METHOD_LABEL: Record<Method, string> = { razorpay: "Pay online (Razorpay)", cash: "Cash", cheque: "Cheque" };

function HealthCartPage() {
  const cart = useHealthCart();
  const session = useCustomerSession();
  const products = useQuery(healthProductsQuery);
  const status = useQuery({
    queryKey: ["health-products", "checkout-status"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("product_checkout_status");
      if (error) throw error;
      return data as unknown as CheckoutStatus;
    },
  });
  const startPay = useServerFn(startProductRazorpayPayment);
  const confirmPay = useServerFn(confirmProductRazorpayPayment);
  const reportFail = useServerFn(reportProductRazorpayFailure);

  const [form, setForm] = useState({ name: "", phone: "", email: "", line1: "", line2: "", city: "", state: "", pincode: "", cheque_reference: "" });
  const [method, setMethod] = useState<Method | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [placed, setPlaced] = useState<Placed | null>(null);
  const idemKey = useRef<string>(crypto.randomUUID());

  const byId = useMemo(() => new Map((products.data ?? []).map((p) => [p.id, p])), [products.data]);
  const lines = cart.lines.map((l) => ({ ...l, p: byId.get(l.productId) }));
  const valid = lines.filter((l) => l.p);
  const missing = products.isSuccess ? lines.filter((l) => !l.p) : [];
  const shipCfg: ShippingConfig = status.data ?? { shipping_model: null, flat_shipping_paise: null, free_shipping_min_paise: null };
  const totals = cartTotals(
    valid.map((l) => ({ unitPaise: l.p!.pricePaise, qty: l.qty, gstRatePercent: l.p!.gstRatePercent, includesGst: l.p!.priceIncludesGst })),
    shipCfg,
  );
  const blocker = status.data?.blocker ?? (status.isError ? "settings_missing" : null);
  const methods = (Object.keys(METHOD_LABEL) as Method[]).filter((m) => status.data?.methods?.[m]);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function payOnline(order: Placed) {
    const ok = await loadRazorpayScript();
    if (!ok || !window.Razorpay) throw new Error("Could not load the payment window. Please try again.");
    const guestKey = session.user ? undefined : idemKey.current;
    const s = await startPay({ data: { orderId: order.order_id, guestKey } });
    await new Promise<void>((resolve) => {
      const rzp = new window.Razorpay!({
        key: s.keyId,
        order_id: s.razorpayOrderId,
        amount: s.amountPaise,
        currency: "INR",
        name: "Poonam Kapur Healthy Kitchen",
        description: `Order ${s.orderNumber}`,
        prefill: { name: s.name, contact: s.phone, email: s.email ?? undefined },
        handler: async (r: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string }) => {
          try {
            const res = await confirmPay({
              data: { orderId: order.order_id, guestKey, razorpayOrderId: r.razorpay_order_id, paymentId: r.razorpay_payment_id, signature: r.razorpay_signature },
            });
            setPlaced({ ...order, payment_status: res.status === "paid" ? "paid" : "awaiting_payment" });
          } catch {
            setError("We couldn't confirm your payment yet. Please don't pay again — contact us with your order number.");
          }
          resolve();
        },
        modal: { ondismiss: () => resolve() },
      });
      rzp.on("payment.failed", (resp: unknown) => {
        const reason = (resp as { error?: { description?: string } })?.error?.description ?? "payment_failed";
        void reportFail({ data: { orderId: order.order_id, guestKey, reason } });
      });
      rzp.open();
    });
  }

  async function placeOrder() {
    if (!method || busy) return;
    setBusy(true);
    setError(null);
    try {
      let order = placed;
      if (!order) {
        const { data, error: err } = await supabase.rpc("place_product_order", {
          _items: valid.map((l) => ({ product_id: l.productId, quantity: l.qty, unit_price_paise: l.p!.pricePaise })),
          _contact: { name: form.name, phone: form.phone, email: form.email, cheque_reference: form.cheque_reference },
          _address: { line1: form.line1, line2: form.line2, city: form.city, state: form.state, pincode: form.pincode },
          _payment_method: method,
          _idempotency_key: idemKey.current,
          _expected_total_paise: totals.totalPaise,
        });
        if (err) throw new Error(orderErrorText(err.message));
        order = data as unknown as Placed;
        cart.clear();
        setPlaced(order);
      }
      if (order.payment_method === "razorpay" && order.payment_status !== "paid") await payOnline(order);
    } catch (e) {
      const msg = (e as Error).message;
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  if (placed && (placed.payment_method !== "razorpay" || placed.payment_status === "paid")) {
    return (
      <SiteLayout>
        <PageHero eyebrow="Health Products" title="Thank you — order received." intro={`Order number ${placed.order_number}`} />
        <Section className="pt-0">
          <div className="mx-auto max-w-xl rounded-2xl border border-border bg-card p-6 text-center">
            <p className="text-lg">Total {formatPaise(placed.total_paise)}</p>
            <p className="mt-2 text-muted-foreground">
              {placed.payment_status === "paid"
                ? "Your payment has been received."
                : placed.payment_method === "cash"
                  ? "Please keep the cash ready. We'll confirm when it's collected."
                  : "We'll confirm your order once the cheque has cleared."}
            </p>
            <Link to="/health-products" className="mt-6 inline-block underline">Continue shopping</Link>
          </div>
        </Section>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <PageHero eyebrow="Health Products" title="Your cart" />
      <Section className="pt-0">
        {products.isLoading || status.isLoading ? (
          <div className="flex min-h-[200px] items-center justify-center" role="status">
            <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden />
          </div>
        ) : products.isError ? (
          <Alert variant="destructive"><AlertDescription>We couldn't load products. Please refresh the page.</AlertDescription></Alert>
        ) : cart.lines.length === 0 && !placed ? (
          <div className="rounded-3xl border border-dashed border-border p-12 text-center">
            <h2 className="font-display text-xl">Your cart is empty</h2>
            <Link to="/health-products" className="mt-4 inline-block underline">Browse health products</Link>
          </div>
        ) : (
          <div className="grid gap-10 lg:grid-cols-[1fr_380px]">
            <div className="space-y-4">
              {missing.length > 0 ? (
                <Alert>
                  <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
                    {missing.length} item{missing.length > 1 ? "s are" : " is"} no longer available.
                    <Button size="sm" variant="outline" onClick={() => missing.forEach((m) => cart.remove(m.productId))}>Remove</Button>
                  </AlertDescription>
                </Alert>
              ) : null}
              {valid.map(({ p, qty, productId }) => {
                const line = priceLine(p!.pricePaise, qty, p!.gstRatePercent, p!.priceIncludesGst);
                return (
                  <div key={productId} className="flex gap-4 rounded-2xl border border-border bg-card p-4">
                    <img src={p!.imageUrl} alt={p!.altText} className="h-20 w-20 rounded-lg object-cover" />
                    <div className="flex flex-1 flex-col gap-2">
                      <div className="flex justify-between gap-2">
                        <div>
                          <p className="font-medium">{p!.name}</p>
                          <p className="text-xs text-muted-foreground">{[packLabel(p!), `SKU ${p!.sku}`].filter(Boolean).join(" · ")}</p>
                        </div>
                        <p className="font-semibold">{formatPaise(line.grossPaise)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="icon" variant="outline" className="h-8 w-8" aria-label={`Decrease ${p!.name}`} onClick={() => cart.setQty(productId, qty - 1)} disabled={!!placed}><Minus className="h-3 w-3" /></Button>
                        <span className="w-8 text-center" aria-live="polite">{qty}</span>
                        <Button size="icon" variant="outline" className="h-8 w-8" aria-label={`Increase ${p!.name}`} onClick={() => cart.setQty(productId, qty + 1)} disabled={qty >= HP_MAX_QTY || !!placed}><Plus className="h-3 w-3" /></Button>
                        <span className="text-xs text-muted-foreground">{formatPaise(p!.pricePaise)} each · {p!.priceIncludesGst ? "incl." : "+"} {p!.gstRatePercent}% GST</span>
                        <Button size="icon" variant="ghost" className="ml-auto h-8 w-8" aria-label={`Remove ${p!.name}`} onClick={() => cart.remove(productId)} disabled={!!placed}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <aside className="h-fit space-y-5 rounded-2xl border border-border bg-card p-5">
              <dl className="space-y-1 text-sm">
                <div className="flex justify-between"><dt>Product amount (before GST)</dt><dd>{formatPaise(totals.itemsTotalPaise - totals.gstTotalPaise)}</dd></div>
                <div className="flex justify-between"><dt>GST</dt><dd>{formatPaise(totals.gstTotalPaise)}</dd></div>
                <div className="flex justify-between"><dt>Shipping charges</dt><dd>{shipCfg.shipping_model ? (totals.shippingPaise ? formatPaise(totals.shippingPaise) : "Free") : "—"}</dd></div>
                <div className="flex justify-between border-t border-border pt-2 text-base font-semibold"><dt>Total payable</dt><dd>{formatPaise(totals.totalPaise)}</dd></div>
              </dl>

              {blocker ? (
                <Alert><AlertDescription>{CHECKOUT_BLOCKER_TEXT[blocker] ?? "Online ordering is not available right now."} Please check back soon.</AlertDescription></Alert>
              ) : (
                <>
                {!session.user ? (
                  <p className="text-sm text-muted-foreground">
                    Checking out as a guest — no account needed. <Link to="/account/sign-in" className="underline">Sign in</Link> instead
                  </p>
                ) : null}
                <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); void placeOrder(); }}>
                  <fieldset disabled={!!placed || busy} className="space-y-3">
                    <Field id="name" label="Full name" value={form.name} onChange={set("name")} autoComplete="name" />
                    <Field id="phone" label="Mobile number" value={form.phone} onChange={set("phone")} autoComplete="tel" inputMode="numeric" maxLength={10} />
                    <Field id="email" label="Email (optional)" value={form.email} onChange={set("email")} autoComplete="email" type="email" />
                    <Field id="line1" label="Address" value={form.line1} onChange={set("line1")} autoComplete="address-line1" />
                    <Field id="line2" label="Address line 2 (optional)" value={form.line2} onChange={set("line2")} autoComplete="address-line2" />
                    <div className="grid grid-cols-2 gap-3">
                      <Field id="city" label="City" value={form.city} onChange={set("city")} autoComplete="address-level2" />
                      <Field id="state" label="State" value={form.state} onChange={set("state")} autoComplete="address-level1" />
                    </div>
                    <Field id="pincode" label="Pincode" value={form.pincode} onChange={set("pincode")} autoComplete="postal-code" inputMode="numeric" maxLength={6} />
                    <div role="radiogroup" aria-label="Payment method" className="space-y-2">
                      <p className="text-sm font-medium">Payment</p>
                      {methods.map((m) => {
                        const belowCash = m === "cash" && status.data?.cash_min_order_paise != null && totals.totalPaise < status.data.cash_min_order_paise;
                        return (
                          <label key={m} className="flex items-center gap-2 text-sm">
                            <input type="radio" name="method" checked={method === m} onChange={() => setMethod(m)} disabled={belowCash} />
                            {METHOD_LABEL[m]}
                            {belowCash ? <span className="text-xs text-muted-foreground">(minimum {formatPaise(status.data!.cash_min_order_paise)})</span> : null}
                          </label>
                        );
                      })}
                    </div>
                    {method === "cheque" ? <Field id="cheque" label="Cheque number (optional)" value={form.cheque_reference} onChange={set("cheque_reference")} /> : null}
                  </fieldset>
                  {error ? <p className="text-sm text-destructive" role="alert">{error}</p> : null}
                  <Button type="submit" className="w-full rounded-full" disabled={!method || busy || (!placed && valid.length === 0)}>
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    {placed ? `Retry payment · ${formatPaise(placed.total_paise)} (order ${placed.order_number})` : `Place order · ${formatPaise(totals.totalPaise)}`}
                  </Button>
                </form>
                </>
              )}
            </aside>
          </div>
        )}
      </Section>
    </SiteLayout>
  );
}

function Field({ id, label, ...rest }: { id: string; label: string } & React.ComponentProps<typeof Input>) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} {...rest} />
    </div>
  );
}
